import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import KanbanCardProductsAPI from '../../api/kanbanCardProducts';

export const state = {
  records: {},
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getCardProducts: $state => cardId => {
    return $state.records[cardId] || [];
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getCardProduct: $state => (cardId, productId) => {
    const cardProducts = $state.records[cardId] || [];
    return cardProducts.find(product => product.id === Number(productId));
  },
  getCardTotalValue: $state => cardId => {
    const cardProducts = $state.records[cardId] || [];
    return cardProducts.reduce((total, product) => {
      // Calcular total_price se não existir
      let totalPrice = product.total_price;
      if (!totalPrice && product.unit_price && product.quantity) {
        totalPrice = parseFloat(product.unit_price) * parseInt(product.quantity);
      }
      
      // Garantir que seja um número válido
      const numericTotal = parseFloat(totalPrice) || 0;
      return total + numericTotal;
    }, 0);
  },
  getCardTotalsByCurrency: $state => cardId => {
    const cardProducts = $state.records[cardId] || [];
    const totals = {};
    
    cardProducts.forEach(product => {
      const currency = product.currency || 'BRL';
      if (!totals[currency]) {
        totals[currency] = 0;
      }
      
      // Calcular total_price se não existir
      let totalPrice = product.total_price;
      if (!totalPrice && product.unit_price && product.quantity) {
        totalPrice = parseFloat(product.unit_price) * parseInt(product.quantity);
      }
      
      // Garantir que seja um número válido
      const numericTotal = parseFloat(totalPrice) || 0;
      totals[currency] += numericTotal;
    });
    
    return totals;
  },
};

export const actions = {
  get: async function getCardProducts({ commit }, { cardId, pipelineId }) {
    commit(types.SET_KANBAN_CARD_PRODUCT_UI_FLAG, { isFetching: true });
    try {
      const response = await KanbanCardProductsAPI.getProducts(pipelineId, cardId);
      // A API retorna { kanban_card_products: [...] }
      const products = response.data?.kanban_card_products || response.data || [];
      commit(types.SET_KANBAN_CARD_PRODUCTS, { cardId, products });
    } catch (error) {
      console.error('Error fetching card products:', error);
      // Em caso de erro, definir array vazio
      commit(types.SET_KANBAN_CARD_PRODUCTS, { cardId, products: [] });
    } finally {
      commit(types.SET_KANBAN_CARD_PRODUCT_UI_FLAG, { isFetching: false });
    }
  },

  create: async function createCardProduct({ commit }, { cardId, pipelineId, productData }) {
    commit(types.SET_KANBAN_CARD_PRODUCT_UI_FLAG, { isCreating: true });
    try {
      console.log('🔄 Store: Criando produto do card:', { cardId, pipelineId, productData });
      const response = await KanbanCardProductsAPI.addProduct(pipelineId, cardId, productData);
      console.log('✅ Store: Produto criado com sucesso:', response.data);
      
      // Se retornou status 200 (OK), significa que somou quantidade
      // Se retornou status 201 (Created), significa que criou novo produto
      if (response.status === 200) {
        // Produto já existia e foi somado - atualizar produto existente
        commit(types.EDIT_KANBAN_CARD_PRODUCT, { cardId, product: response.data });
      } else {
        // Produto novo criado - adicionar à lista
        commit(types.ADD_KANBAN_CARD_PRODUCT, { cardId, product: response.data });
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Store: Erro ao criar produto:', error);
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_CARD_PRODUCT_UI_FLAG, { isCreating: false });
    }
  },

  update: async function updateCardProduct({ commit }, { cardId, pipelineId, productId, productData }) {
    commit(types.SET_KANBAN_CARD_PRODUCT_UI_FLAG, { isUpdating: true });
    try {
      const response = await KanbanCardProductsAPI.updateProduct(pipelineId, cardId, productId, productData);
      commit(types.EDIT_KANBAN_CARD_PRODUCT, { cardId, product: response.data });
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_CARD_PRODUCT_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deleteCardProduct({ commit }, { cardId, pipelineId, productId }) {
    commit(types.SET_KANBAN_CARD_PRODUCT_UI_FLAG, { isDeleting: true });
    try {
      await KanbanCardProductsAPI.removeProduct(pipelineId, cardId, productId);
      commit(types.DELETE_KANBAN_CARD_PRODUCT, { cardId, productId });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_KANBAN_CARD_PRODUCT_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_KANBAN_CARD_PRODUCT_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },

  [types.SET_KANBAN_CARD_PRODUCTS]($state, { cardId, products }) {
    $state.records = {
      ...$state.records,
      [cardId]: products,
    };
  },

  [types.ADD_KANBAN_CARD_PRODUCT]($state, { cardId, product }) {
    const existingProducts = $state.records[cardId] || [];
    $state.records = {
      ...$state.records,
      [cardId]: [...existingProducts, product],
    };
  },

  [types.EDIT_KANBAN_CARD_PRODUCT]($state, { cardId, product }) {
    const cardProducts = $state.records[cardId] || [];
    const index = cardProducts.findIndex(p => p.id === product.id);
    if (index !== -1) {
      cardProducts[index] = product;
      $state.records = {
        ...$state.records,
        [cardId]: [...cardProducts],
      };
    }
  },

  [types.DELETE_KANBAN_CARD_PRODUCT]($state, { cardId, productId }) {
    const cardProducts = $state.records[cardId] || [];
    const filteredProducts = cardProducts.filter(p => p.id !== Number(productId));
    $state.records = {
      ...$state.records,
      [cardId]: filteredProducts,
    };
  },

  [types.CLEAR_KANBAN_CARD_PRODUCTS]($state, cardId) {
    const newRecords = { ...$state.records };
    delete newRecords[cardId];
    $state.records = newRecords;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};